import type * as React from "react";
import { cn } from "@/lib/utils";

interface Column<T> {
    key: string;
    header: React.ReactNode;
    cell: (row: T) => React.ReactNode;
    className?: string;
    headerClassName?: string;
}

interface DataTableProps<T> {
    columns: Column<T>[];
    rows: T[];
    rowKey: (row: T) => string | number;
    onRowClick?: (row: T) => void;
    emptyState?: React.ReactNode;
    className?: string;
    dataTestid?: string;
}

export function DataTable<T>({
    columns,
    rows,
    rowKey,
    onRowClick,
    emptyState,
    className,
    dataTestid,
}: DataTableProps<T>): React.ReactNode {
    if (rows.length === 0 && emptyState) {
        return <div data-testid={dataTestid}>{emptyState}</div>;
    }

    return (
        <div
            className={cn("overflow-x-auto rounded-lg border bg-card", className)}
            data-testid={dataTestid}
        >
            <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={cn(
                                    "whitespace-nowrap px-4 py-3 text-left font-medium text-muted-foreground",
                                    col.headerClassName
                                )}
                            >
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={rowKey(row)}
                            onClick={() => onRowClick?.(row)}
                            className={cn(
                                "border-b last:border-b-0 transition-colors hover:bg-accent/40",
                                onRowClick && "cursor-pointer"
                            )}
                        >
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    className={cn("whitespace-nowrap px-4 py-3", col.className)}
                                >
                                    {col.cell(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
